import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layout } from '../components/layout/Layout';
import { PageHero, ContentSection, SEOContent, CTASection } from '../components/shared';
import { pageHeroImages } from '../data/mock';
import { ArrowRight, ArrowDown } from 'lucide-react';

const vorsorgeFeatures = [
  {
    id: 'checkup',
    title: 'Gesundheits-Check-up',
    description: 'Der regelmäßige Gesundheits-Check für Herz, Nieren und Stoffwechsel.',
    image: '/images/Checkup35_neu.webp'
  },
  {
    id: 'krebs',
    title: 'Krebsvorsorge Männer & Frauen',
    description: 'Spezialisierte Früherkennung für Männer und Frauen.',
    image: '/images/Krebsvorsorge_Maenner2.webp'
  },
  {
    id: 'haut',
    title: 'Hautkrebsscreening',
    description: 'Früherkennung von Hautveränderungen – schmerzfrei und effektiv.',
    image: '/images/Hautkrebs.webp'
  },
  {
    id: 'impfung',
    title: 'Impfberatung & Impfungen',
    description: 'Schutzimpfungen und individuelle Impfplanung.',
    image: '/images/IMPFEN.webp'
  },
  {
    id: 'herz',
    title: 'Herzvorsorge / Präventiver Herz-Check',
    description: 'Kardiologische Vorsorge für ein gesundes Herz.',
    image: '/images/2C_Hero_Kardiologie.webp'
  },
  {
    id: 'reise',
    title: 'Reise- & Berufsimpfungen',
    description: 'Optimaler Impfschutz für Reisen und Beruf.',
    image: '/images/Content_Behandlung_5.webp'
  }
];

export const VorsorgePage = () => {
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Layout>
      <PageHero
        title="Vorsorge & Prävention im Medicum Diez"
        subtitle="Gesund bleiben durch Früherkennung, Beratung und regelmäßige Untersuchungen."
        backgroundImage={pageHeroImages.vorsorge}
        ctaText="Vorsorgetermin buchen"
      />

      {/* Schnellnavigation - Anchor Buttons */}
      <section className="py-6 bg-[#032925]">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => scrollToSection('leistungen')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#edd68d] hover:bg-[#d4c17d] rounded-full text-[#032925] text-sm font-medium transition-colors"
            >
              Unsere Leistungen
              <ArrowDown className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollToSection('checkup')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#66f2ae] hover:bg-[#50d899] rounded-full text-black text-sm font-medium transition-colors"
            >
              Check-up
              <ArrowDown className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollToSection('krebs')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#66f2ae] hover:bg-[#50d899] rounded-full text-black text-sm font-medium transition-colors"
            >
              Krebsvorsorge
              <ArrowDown className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollToSection('impfung')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#66f2ae] hover:bg-[#50d899] rounded-full text-black text-sm font-medium transition-colors"
            >
              Impfungen
              <ArrowDown className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollToSection('herz')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#66f2ae] hover:bg-[#50d899] rounded-full text-black text-sm font-medium transition-colors"
            >
              Herzvorsorge
              <ArrowDown className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollToSection('termin')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#edd68d] hover:bg-[#d4c17d] rounded-full text-[#032925] text-sm font-medium transition-colors"
            >
              Termin buchen
              <ArrowDown className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Feature Grid mit klickbaren Karten */}
      <section id="leistungen" className="py-16 lg:py-20 bg-[#f9f3e2]">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl font-semibold text-[#032925]">
              Unsere Vorsorgeleistungen
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {vorsorgeFeatures.map((feature, index) => (
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#8ae9bb]/50 rounded-2xl overflow-hidden shadow-lg cursor-pointer hover:shadow-xl transition-all"
                onClick={() => scrollToSection(feature.id)}
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={feature.image}
                    alt={feature.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-[#032925] mb-2">{feature.title}</h3>
                  <p className="text-[#032925]/70 text-sm mb-4">{feature.description}</p>
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#032925] hover:text-[#1a1308]">
                    Mehr erfahren
                    <ArrowDown className="w-4 h-4" />
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Check-up Content */}
      <div id="checkup">
        <ContentSection
          title="Gesundheits-Check-up"
          content="Der Gesundheits-Check-up ist eine wichtige Vorsorgeuntersuchung zur Früherkennung von Herz-Kreislauf-Erkrankungen, Diabetes und Nierenerkrankungen. Ab 35 Jahren haben Sie alle drei Jahre Anspruch auf diese Untersuchung. Wir prüfen Blutwerte, Blutdruck, Gewicht und führen ein ausführliches Gespräch zu Ihrem Lebensstil."
          image="/images/Checkup35_neu.webp"
          backgroundColor="white"
        />
      </div>

      {/* Krebsvorsorge Content */}
      <div id="krebs">
        <ContentSection
          title="Krebsvorsorge Männer & Frauen"
          content="Früherkennungsuntersuchungen können Leben retten. Wir bieten geschlechtsspezifische Vorsorgeprogramme für Männer und Frauen an – von der Tastuntersuchung bis zur Labordiagnostik. Sprechen Sie uns an, welche Untersuchungen für Sie sinnvoll sind."
          image="/images/Krebsvorsorge_Maenner2.webp"
          imagePosition="left"
          backgroundColor="gray"
        />
      </div>

      {/* Hautkrebsscreening Content */}
      <div id="haut">
        <ContentSection
          title="Hautkrebsscreening"
          content="Das Hautkrebsscreening ist eine schmerzfreie Untersuchung der gesamten Haut zur Früherkennung von Hautkrebs. Ab 35 Jahren können Sie alle zwei Jahre eine solche Untersuchung durchführen lassen. Bei auffälligen Befunden überweisen wir Sie zur weiteren Abklärung."
          image="/images/Hautkrebs.webp"
          backgroundColor="white"
        />
      </div>

      {/* Impfberatung Content */}
      <div id="impfung">
        <ContentSection
          title="Impfberatung & Impfungen"
          content="Schutzimpfungen gehören zu den wichtigsten Maßnahmen der Prävention. Wir überprüfen Ihren Impfstatus, beraten Sie zu empfohlenen Auffrischungen und führen Impfungen direkt in unserer Praxis durch. Bringen Sie Ihren Impfpass zum Termin mit."
          image="/images/IMPFEN.webp"
          imagePosition="left"
          backgroundColor="gray"
        />
      </div>

      {/* Herzvorsorge Content */}
      <div id="herz">
        <ContentSection
          title="Herzvorsorge / Präventiver Herz-Check"
          content="Herz-Kreislauf-Erkrankungen sind die häufigste Todesursache in Deutschland. Mit einem präventiven Herz-Check können Risikofaktoren frühzeitig erkannt werden. In Zusammenarbeit mit unserer Kardiologie bieten wir EKG, Belastungs-EKG und weitere kardiologische Diagnostik an."
          image="/images/2C_Hero_Kardiologie.webp"
          backgroundColor="white"
        />
      </div>

      {/* Reiseimpfungen Content */}
      <div id="reise">
        <ContentSection
          title="Reise- & Berufsimpfungen"
          content="Je nach Reiseziel oder beruflicher Tätigkeit können spezielle Impfungen erforderlich sein. Wir beraten Sie individuell zu Hepatitis, Typhus, Tollwut und anderen Reiseimpfungen und stellen die notwendigen Bescheinigungen aus."
          image="/images/Content_Behandlung_5.webp"
          imagePosition="left"
          backgroundColor="gray"
        />
      </div>

      {/* Vorsorge als Grundlage */}
      <ContentSection
        title="Vorsorge als Grundlage für langfristige Gesundheit"
        content="Regelmäßige Vorsorgeuntersuchungen helfen, Erkrankungen frühzeitig zu erkennen und gezielt vorzubeugen. Wir beraten Sie individuell und stellen Ihren persönlichen Vorsorgeplan zusammen – abgestimmt auf Ihr Alter, Ihre Vorgeschichte und Ihre persönlichen Risikofaktoren."
        image="/images/ganze Familie.webp"
        backgroundColor="white"
      />

      {/* Zusammenarbeit */}
      <section className="py-16 lg:py-20 bg-[#f9f3e2]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl md:text-3xl font-semibold mb-6 text-[#032925]">
                Enge Zusammenarbeit mit Diagnostik und Kardiologie
              </h2>
              <p className="text-[#032925]/80 leading-relaxed mb-6">
                Je nach Fragestellung kombinieren wir Vorsorgeuntersuchungen mit moderner Diagnostik und kardiologischer Abklärung direkt im Medicum Diez. Kurze Wege, schnelle Befunde und abgestimmte Behandlung – alles unter einem Dach.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/leistungen/diagnostik" className="inline-flex items-center gap-2 px-6 py-3 bg-[#8ae9bb]/50 hover:bg-[#8ae9bb]/70 rounded-full text-[#032925] font-medium transition-colors">
                  Diagnostik-Zentrum
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/leistungen/kardiologie" className="inline-flex items-center gap-2 px-6 py-3 bg-[#8ae9bb]/50 hover:bg-[#8ae9bb]/70 rounded-full text-[#032925] font-medium transition-colors">
                  Kardiologie & Herz-Check
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="/images/Kardiologie 2.webp"
                  alt="Kardiologie"
                  className="w-full h-auto object-cover aspect-[4/3]"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <SEOContent
        title="Vorsorgeuntersuchungen in Diez und Limburg"
        content="Im Medicum Diez bieten wir Gesundheits-Check-ups, Krebsvorsorge, Hautkrebsscreening, Impfberatung und Herzvorsorge. Unsere Hausärzte koordinieren Prävention und Diagnostik für eine langfristige Gesundheitsvorsorge im Rhein-Lahn-Kreis."
      />

      <div id="termin">
        <CTASection
          title="Vorsorgetermin vereinbaren"
          subtitle="Vereinbaren Sie Ihren Termin zur Vorsorge und Prävention online oder telefonisch."
          buttonText="Vorsorgetermin buchen"
        />
      </div>
    </Layout>
  );
};

export default VorsorgePage;
